import * as React from 'react';
import isEqual from 'lodash/isEqual';
import {useDispatch, useSelector} from 'react-redux';

import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {Banner} from '@spaced-out/ui-design-system/lib/components/Banner';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {selectAgencyData} from 'src/selectors/agency';
import {startDataExport} from 'src/action-creators/data-export-report';
import {EXPORT_BYOD_WIDGET} from 'src/utils/analytics-genesis';

import {ByodErrorBoundary} from 'src/components/analytics-genesis/byod/components/byod-error-boundary';
import {trackReportExported} from 'src/components/analytics-genesis/byod/analytics/byod-amplitude-track';
import {resolveTileFilters} from 'src/components/analytics-genesis/byod/utils/resolve-tile-filters';
import {Canvas} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/canvas/canvas';
import type {ByodFilterMap} from 'src/types/byod';

import {useByodDashboardData} from './useByodDashboardData';
import {useByodEmbedPayload} from './useByodEmbedPayload';
import {useByodWidgetOverrides} from './useByodWidgetOverrides';
import type {
  ByodDashboardViewDateRange,
  ByodDashboardViewProps,
} from './byod-dashboard-view.types';
import css from './byod-dashboard-view.css';


const EMPTY_PAYLOAD: ByodFilterMap = {};

// ---------------------------------------------------------------------------
// ByodDashboardViewInner — presentational shell. The data lifecycle (load,
// hydrate into the keyed `dashboardViewStore`, telemetry) lives in
// `useByodDashboardData`; the per-instance query inputs (date-window validation,
// effective payload) live in `useByodEmbedPayload`. This component only maps the
// resulting state to loading / error / empty / canvas UI. The builder's separate
// `dashboardStore` is never touched, so embeds and a live builder coexist with
// zero cross-talk.
// ---------------------------------------------------------------------------

function ByodDashboardViewInner({
  dashboardId,
  dateRange,
  globalPayload,
}: {
  dashboardId: string;
  dateRange: ByodDashboardViewDateRange;
  globalPayload: ByodFilterMap;
}) {
  const {t} = useLabelTranslation();
  const {isLoading, isError, refetch, hydrated, hydrationFailed} =
    useByodDashboardData(dashboardId);
  const {isDateWindowBounded, effectivePayload} = useByodEmbedPayload({
    dashboardId,
    dateRange,
    globalPayload,
  });
  // Local, session-only column overrides (the embed's "edit table columns"):
  // stored in the keyed dashboardViewStore and layered over the hydrated widgets.
  const {widgets, onUpdateWidget} = useByodWidgetOverrides(
    dashboardId,
    hydrated?.params.widgets,
  );

  const dispatch = useDispatch();
  const {id: agencyId, name: agencyName} = useSelector(selectAgencyData);

  // The embed is read-only but still supports CSV export per tile (same action
  // the builder uses). Filters honor exactly what the tile shows: its saved
  // filters + the host-injected payload, via the shared read-path transform.
  const handleExport = React.useCallback(
    (tileId: string) => {
      const widget = widgets[tileId];
      if (!widget) {
        return;
      }
      trackReportExported({
        agency_id: agencyId,
        agency_name: agencyName,
        reportName: widget.label ?? tileId,
        mode: 'dashboard',
        dashboardId,
      });
      void dispatch(
        startDataExport(
          EXPORT_BYOD_WIDGET,
          {
            widget_key: widget.widget_key,
            widget_name: widget.label ?? tileId,
            dashboard_id: dashboardId,
            placement_id: tileId,
            filters: resolveTileFilters(
              widget.widget_filters_json,
              effectivePayload,
              widget.supported_filters,
            ),
          },
          {},
          undefined,
          'genesis',
        ),
      );
    },
    [dispatch, agencyId, agencyName, dashboardId, widgets, effectivePayload],
  );

  // Missing date bound: reported to Sentry inside useByodEmbedPayload. Render a
  // failure state rather than falling through and silently issuing an unbounded
  // /query. Retrying can't add a bound the host never supplied, so no retry
  // action.
  if (!isDateWindowBounded) {
    return (
      <div className={css.statusContainer}>
        <Banner semantic="danger">
          {t(
            'Something_went_wrong_please_refresh_the_page',
            'Something went wrong, please refresh the page',
          )}
        </Banner>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={css.statusContainer}>
        <CircularLoader size="medium" />
      </div>
    );
  }

  // Network load failure — retrying the request may recover it.
  if (isError) {
    return (
      <div className={css.statusContainer}>
        <Banner
          semantic="danger"
          actionText={t('Retry', 'Retry')}
          onAction={refetch}
        >
          {t(
            'Failed_to_load_dashboard_Please_try_again',
            'Failed to load dashboard. Please try again.',
          )}
        </Banner>
      </div>
    );
  }

  // Hydration/parse failure (or an unexpected empty hydration) — the load
  // succeeded, so an in-place network Retry would be a no-op loop. Surface a
  // distinct, action-less state instead.
  if (hydrationFailed || !hydrated) {
    return (
      <div className={css.statusContainer}>
        <Banner semantic="danger">
          {t(
            'Something_went_wrong_please_refresh_the_page',
            'Something went wrong, please refresh the page',
          )}
        </Banner>
      </div>
    );
  }

  const {rowOrder, rows} = hydrated.params;
  const {orphanedWidgetCount} = hydrated;

  return (
    <div className={css.viewRoot} data-testid="byod-dashboard-view">
      {orphanedWidgetCount > 0 && (
        <Banner semantic="information">
          {t(
            'count_report_s_have_missing_definitions_and_may_not_display_correctly',
            {
              count: orphanedWidgetCount,
              defaultValue: `${orphanedWidgetCount} report(s) have missing definitions and may not display correctly.`,
            },
          )}
        </Banner>
      )}
      {/* The SAME <Canvas> the builder uses — read-only (no drag/edit/remove).
          `onUpdateWidget` stores the viewer's local column picks in the
          dashboardViewStore (session-only); `onExport` enables per-tile CSV
          export. Scheduling needs no handler here — the tile offers it in view
          mode on a saved dashboard on its own. */}
      <Canvas
        rowOrder={rowOrder}
        rows={rows}
        widgets={widgets}
        editable={false}
        dashboardId={dashboardId}
        filters={effectivePayload}
        onUpdateWidget={onUpdateWidget}
        onExport={handleExport}
      />
    </div>
  );
}

/**
 * Embeddable, READ-ONLY BYOD dashboard viewer — "a canvas usable anywhere."
 *
 * Renders any saved dashboard by id with all filters injected via `globalPayload`
 * (no date picker, no filter UI, no edit affordances). Hydrated dashboard data
 * lives in the keyed `dashboardViewStore` (`byId[dashboardId]`), so each id maps
 * to its own data and multiple `<ByodDashboardView>`s coexist on one page
 * without interfering (the builder's separate store is never touched). Tiles
 * render through the SAME `<Canvas>` / `<DashboardTile>` as the builder, so a
 * layout/tile/style change reflects in both.
 */
export function ByodDashboardView({
  dashboardId,
  dateRange,
  globalPayload,
}: ByodDashboardViewProps) {
  // Stabilize the opaque payload by content so unrelated host re-renders don't
  // cascade into every tile's query key. (dateRange flows through as primitives.)
  const incoming = globalPayload ?? EMPTY_PAYLOAD;
  const payloadRef = React.useRef(incoming);
  if (!isEqual(payloadRef.current, incoming)) {
    payloadRef.current = incoming;
  }
  const stablePayload = payloadRef.current;

  return (
    <ByodErrorBoundary variant="page" resetKey={dashboardId}>
      <ByodDashboardViewInner
        dashboardId={dashboardId}
        dateRange={dateRange}
        globalPayload={stablePayload}
      />
    </ByodErrorBoundary>
  );
}
